import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Field,
	getFormValues,
	reduxForm,
	change
} from "redux-form";
import { Blade, Button, ProgressIndicator, ReduxControls } from "rhombus-react";
import { useFlag } from "@innovid/feature-flags-react";

import { FEATURE_FLAGS } from "featureFlag/featureFlag";
import useActiveCreative from "hooks/useActiveCreative";

import { renameCreative, renameCreativeFilename } from "reducers/Creatives";
import { closeModal } from 'reducers/Modal';
import { labelWithCount, safeDecodeURIComponent } from "utils/common";
import {
	creativeNameMaxLength,
	creativeNameAllowedChars,
	creativeNameAllowedCharsLegacyInnovid,
	creativeFilenameMaxLength,
	CREATIVE_NAME_MAX_LENGTH,
	CREATIVE_FILENAME_MAX_LENGTH
} from 'widgets/forms/validators/Creatives';
import { required, validFilenameCharacters } from "widgets/forms/validators/validators";

import "./RenameCreatives.css";

const RENAME_CREATIVES_FORM = "rename-creatives-form";

function RenameCreatives(props){
	let dispatch = useDispatch()
		, { creatives = [], libraryId, propName, handleSubmit, valid } = props
		, [creative] = creatives //assumes only 1 creative
		, modalTitle = `Rename Creative - ${safeDecodeURIComponent(creatives[0].name)}`
		, { isUpdating, formValues } = useSelector(state => ({
			isUpdating: state.creatives.isUpdating,
			formValues: getFormValues(RENAME_CREATIVES_FORM)(state),
		}))
		, isFilenameMode = propName === 'fileName'
		, supportLegacyInnovidSpecialChars = useFlag(FEATURE_FLAGS.SUPPORT_LEGACY_INNOVID_SPECIAL_CHARS)
		, creativeNameValidators = [
			required,
			creativeNameMaxLength,
			supportLegacyInnovidSpecialChars ? creativeNameAllowedCharsLegacyInnovid : creativeNameAllowedChars
		]
		, fileNameValidators = [required, creativeFilenameMaxLength, validFilenameCharacters]
		, onClose = () => dispatch(closeModal())
		, submitCreativeName = () => dispatch(renameCreative({
			libraryId,
			creativeId: creative.id,
			newName: formValues.newName,
		}))
		, submitFileName = () => dispatch(renameCreativeFilename({
			libraryId,
			creativeId: creative.id,
			newFilename: formValues.newFilename,
		}))
		, onSubmit = () => {
			let submit = isFilenameMode ? submitFileName : submitCreativeName;
			submit().then((response) => {
				if (response?.success) {
					return dispatch(closeModal());
				}
			});
		}
		;

	const fieldName = isFilenameMode ? "newFilename" : "newName";
	const defaultValue = isFilenameMode ? creative.fileName : safeDecodeURIComponent(creative.name);

	useActiveCreative(creative.id);
	useEffect(() => {
		dispatch(change(RENAME_CREATIVES_FORM, fieldName, defaultValue));
	}, []);

	const renderContent = () => (
		<div className="rename-creative-form">
			<ProgressIndicator isLoading={isUpdating} />
			<Field
				name={fieldName}
				label={isFilenameMode ? "New Name for Creative File" : "New Name for Creative"}
				component={ReduxControls.Input}
				placeholder={defaultValue}
				required
				autoFocus
				showCharCount
				charMax={isFilenameMode ? CREATIVE_FILENAME_MAX_LENGTH : CREATIVE_NAME_MAX_LENGTH}
				validate={isFilenameMode ? fileNameValidators : creativeNameValidators}
			/>
		</div>
	);

	const renderFooter = () => ([
		<Button key="cancel" theme="default" disabled={isUpdating} onClick={onClose}>Cancel</Button>,
		<Button key="submit" theme="green" disabled={!valid || isUpdating} onClick={handleSubmit(onSubmit)}>
			{labelWithCount(isFilenameMode ? 'Rename Creative Filename' : 'Rename Creative', creatives.length)}
		</Button>
	]);

	return (
		<Blade
			type="modal"
			setAppTitle={false}
			className="rename-creatives"
			title={modalTitle}
			allowFullScreen={false}
			xClose
			bladeProps={{ verticallyCenter: true }}
			onClose={onClose}
			renderContent={renderContent}
			renderFooter={renderFooter}
		/>
	);
}

RenameCreatives.displayName = "RenameCreatives";


export default reduxForm({
	form: RENAME_CREATIVES_FORM,
})(RenameCreatives);
